import { useEffect, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { v4 as uuidv4 } from 'uuid';
import api from "../api"
import { FileType } from "../common/constants"
import Button from "./Button"
import styles from "./Uploader.module.css"
import ReactPlayer from "react-player"
import { Each } from "../common/Each"
import FileUpload from "./FileUpload";
import Collapsable from "./Collapsable";
import ProgressBar from "./ProgressBar";
import { ReactComponent as InsertDocumentIllustration } from "../assets/images/illustrations/il-insert-document.svg"
import { ReactComponent as RetryIcon } from "../assets/images/icons/ic-restart.svg"
import ImageCropper from "./ImageCropper";
import { ReactComponent as UploadIcon } from "../assets/images/icons/ic-upload.svg"
import { ReactComponent as CropIcon } from "../assets/images/icons/ic-crop.svg"
import { uploadMultipart } from "../utils";

const Uploader = ({
    type = FileType.All,
    label,
    message,
    icon,
    multiple = false,
    reset,
    preview,
    Preview,
    crop = false,
    shape = 'rect',
    aspect = 16 / 9,
    iconButtons = false,
    subdirectory = null,
    add_timestamp = true,
    onStart = () => { },
    onProgress = () => { },
    onComplete = () => { },
    onDelete = () => { } }) => {

    const ref = useRef()
    const [uploads, setUploads] = useState([])
    const [totalProgress, setTotalProgress] = useState(0)
    const [uploading, setUploading] = useState(false)
    const [cropMode, setCropMode] = useState(false)
    const [cropImage, setCropImage] = useState(null)
    const [activeUploads, setActiveUploads] = useState(0)
    const uploadQueue = useRef([])

    const extensions = useMemo(() => {
        switch (type) {
            case FileType.All:
                return {}
            case FileType.Video:
                return {
                    'video/mp4': ['.mp4', '.MP4'],
                    "video/mov": ['.mov'],
                    "video/m4v": ['.m4v'],
                    "video/mpeg": ['.mpeg'],
                }
            case FileType.Image:
                return {
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png'],
                    'image/webp': ['.webp'],
                    'image/heic': ['.heic']
                }
            default: return {}
        }
    }, [type])

    const msg = useMemo(() => {
        if (message) {
            return message
        }
        else {
            switch (type) {
                case FileType.All:
                    return multiple ? 'Trascina o seleziona i file.' : 'Trascina o seleziona il file'
                case FileType.Image:
                    return multiple ? 'Trascina o seleziona le immagini' : 'Trascina o seleziona l\'immagine'
                case FileType.Video:
                    return multiple ? 'Trascina o seleziona i video' : 'Trascina o seleziona il video'
                default: return 'Trascina o seleziona file'

            }
        }
    }, [type, message, multiple])

    const lbl = useMemo(() => {
        if (label) {
            return label
        }
        else {
            switch (type) {
                case FileType.All:
                    return 'SCEGLI FILE'
                case FileType.Image:
                    return multiple ? 'SCELI IMMAGINI' : 'SCEGLI IMMAGINE'
                case FileType.Video:
                    return 'SCEGLI VIDEO'
                default: return 'SCEGLI FILE'
            }
        }
    }, [type, label, multiple])



    // Gestione upload concorrenti (max 10)
    useEffect(() => {
        if (uploads && uploads.length > 0) {
            const sumProgress = uploads.reduce((acc, curr) => { return acc += curr.progress }, 0)
            const totalProgress = parseInt((sumProgress / (uploads.length * 100)) * 100)
            setTotalProgress(totalProgress)
            onProgress(totalProgress, uploads)
        }

        // Buffer upload: max 10 attivi
        const pending = uploads.filter(u => u.status === 'pending')
        if (pending.length > 0 && activeUploads < 10) {
            const toStart = Math.min(10 - activeUploads, pending.length)
            const nextUploads = pending.slice(0, toStart)
            nextUploads.forEach(u => {
                uploadQueue.current.push(u)
            })
            processUploadQueue()
        }
        // eslint-disable-next-line
    }, [uploads, activeUploads])

    // Funzione per processare la coda
    const processUploadQueue = () => {
        if (activeUploads >= 10) return
        if (uploadQueue.current.length === 0) return
        while (activeUploads < 10 && uploadQueue.current.length > 0) {
            const next = uploadQueue.current.shift()
            if (next) {
                setActiveUploads(a => a + 1)
                upload(next).finally(() => {
                    setActiveUploads(a => a - 1)
                })
            }
        }
    }

    const removeFile = async (url) => {
        try {
            let deleted = await api.delete(`/teacher/upload?url=${url}`)
            if (deleted) {
                const nextUploads = uploads.filter(u => u.url !== url)
                setUploads(nextUploads)
                onDelete(nextUploads)
            }
        }
        catch (e) {
            console.error(e)
        }
    }

    const retryUpload = (uploadId) => {
        setUploads((prev) => prev.map((u) => {
            if (u.uploadId !== uploadId) return u
            return {
                ...u,
                progress: 0,
                status: 'pending',
                url: null
            }
        }))
    }

    const retryFailedUploads = () => {
        setUploads((prev) => prev.map((u) => {
            if (u.status !== 'failed') return u
            return {
                ...u,
                progress: 0,
                status: 'pending',
                url: null
            }
        }))
    }

    const upload = async (uploadData) => {
        setUploading(true)
        try {
            onStart(uploadData)
            setUploads((prev) => {
                for (let p of prev) {
                    if (p.uploadId === uploadData.uploadId) {
                        p.status = 'in_progress'
                    }
                }
                return [...prev]
            })
            let upload = await uploadMultipart(uploadData.file, subdirectory, add_timestamp, (progress) => {
                setUploads((prev) => {
                    for (let p of prev) {
                        if (p.uploadId === uploadData.uploadId) {
                            p.progress = progress
                        }
                    }
                    return [...prev]
                })
            })

            setUploads((prev) => {
                for (let p of prev) {
                    if (p.uploadId === uploadData.uploadId) {
                        p.url = upload.url
                        p.status = 'completed'
                    }
                }
                return [...prev]
            })

        } catch (e) {
            console.error(e)
            setUploads((prev) => {
                for (let p of prev) {
                    if (p.uploadId === uploadData.uploadId) {
                        p.status = 'failed'
                    }
                }
                return [...prev]
            })
        }
    }

    const getUrlMediaType = (url) => {
        if (!url) return null
        if (typeof url !== 'string') return null
        const parts = url.split('/');
        const fileName = parts[parts.length - 1]
        const file_parts = fileName.split('.')
        const extension = file_parts[file_parts.length - 1].toLowerCase()
        const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'heic', 'raw']
        const videoExtensions = ['mp4', 'mov', 'mkv', 'avi']
        if (imageExtensions.includes(extension)) {
            return 'image'
        }
        else if (videoExtensions.includes(extension) || type === FileType.Video) {
            return 'video'
        }
        else {
            return 'file'
        }
    }

    useEffect(() => {
        if (totalProgress > 0) {
            setUploading(true)
        }
        const allCompleted = uploads.length > 0 && uploads.every(u => u.status === 'completed')
        const allFinished = uploads.length > 0 && uploads.every(u => u.status === 'completed' || u.status === 'failed')

        if (allCompleted) {
            setUploading(false)
            onComplete(uploads)
        }

        if (allFinished && activeUploads === 0) {
            setUploading(false)
        }
    }, [totalProgress, uploads, onComplete, activeUploads])

    useEffect(() => {
        setUploads([])
        setTotalProgress(0)
    }, [reset])

    const convertBlobToFile = async (blobUrl, fileName) => {
        // Recupera il Blob utilizzando fetch
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        // Crea un oggetto File a partire dal Blob
        const file = new File([blob], fileName, {
            type: blob.type, // Mantieni il tipo MIME del blob
            lastModified: Date.now(), // Imposta una data di modifica
        });

        return file;
    };

    return (
        <Dropzone
            noClick={!!preview || cropMode}
            noKeyboard={!!preview || cropMode}
            ref={ref}
            multiple={multiple}
            onDrop={async (files) => {
                if (crop) {
                    for (let u of uploads) {
                        if (u.url) {
                            await removeFile(u.url)
                        }
                    }
                    setCropMode(true)
                    setCropImage(files[0])
                }
                else {
                    let _uploads = files.map(f => {
                        return {
                            file: f,
                            progress: 0,
                            uploadId: uuidv4(),
                            status: 'pending',
                            url: null
                        }
                    })

                    if (multiple) {
                        setUploads([...uploads, ..._uploads])
                    }
                    else {
                        for (let u of uploads) {
                            if (u.url) {
                                await removeFile(u.url)
                            }
                        }
                        setUploads([..._uploads])
                    }
                }
            }}
            accept={extensions}
            onError={(e) => {
                console.error(e)
            }}
        >

            {({ getRootProps, getInputProps }) => (
                <section {...getRootProps()} className={styles.dropzoneContainer}>
                    <input {...getInputProps()} />
                    <div className={styles.dropzone}>
                        {
                            uploads.length === 0 && !preview &&
                            <InsertDocumentIllustration />
                        }
                        {/* Show ProgressBar and Collapsable only if multiple uploads */}
                        {multiple && uploads.length > 1 && (
                            <div className={styles.dropzoneProgressLabel}>
                                <ProgressBar progress={totalProgress} />
                                <div className={styles.dropzoneUploadLabel}>
                                    <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#888' }}>
                                        {uploads.filter(u => u.status === 'completed').length} / {uploads.length} caricati
                                    </div>
                                    {uploads.some(u => u.status === 'failed') && (
                                        <button
                                            type="button"
                                            className={styles.retryFailed}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                retryFailedUploads()
                                            }}
                                        >
                                            <RetryIcon className={styles.retryIcon} /> Riprova Falliti
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Collapsable for multiple uploads */}
                        {multiple && uploads.length > 1 ? (
                            <Collapsable title={`File: (${uploads.length})`} appearance="transparent">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', width: '100%', maxHeight: '300px', overflow: 'scroll' }}>
                                    <Each of={uploads} render={(upload) => (
                                        <FileUpload
                                            file={upload.file}
                                            url={upload.url}
                                            progress={upload.progress}
                                            status={upload.status}
                                            onDelete={removeFile}
                                            onRetry={retryUpload}
                                            uploadId={upload.uploadId}
                                        />
                                    )} />
                                </div>
                            </Collapsable>
                        ) : (
                            <Each of={uploads} render={(upload) => (
                                <FileUpload
                                    file={upload.file}
                                    url={upload.url}
                                    progress={upload.progress}
                                    status={upload.status}
                                    onDelete={removeFile}
                                    onRetry={retryUpload}
                                    uploadId={upload.uploadId}
                                />
                            )} />
                        )}
                        {preview && !uploading && !multiple && !Preview && !cropMode &&
                            <>
                                {getUrlMediaType(preview) === FileType.Video &&
                                    <ReactPlayer
                                        url={preview}
                                        controls
                                        width="100%"
                                        height="100%"
                                        className={styles.player}
                                    />
                                }
                                {getUrlMediaType(preview) === FileType.Image &&
                                    <div className={styles.thumbnailWrapper}>
                                        <img src={preview} alt="" className={styles.thumbnail} style={{ backgroundColor: '#EDEDED' }} />
                                    </div>
                                }
                            </>
                        }
                        {cropMode &&
                            <>
                                <ImageCropper
                                    shape={shape}
                                    aspect={aspect}
                                    src={cropImage ? URL.createObjectURL(cropImage) : preview}
                                    onSave={async (image) => {
                                        setCropMode(false)
                                        let name = preview ? preview.split('/').pop().split('.')[0] + '.png' : cropImage.name.split('.')[0] + '.png'
                                        let f = await convertBlobToFile(image, name)
                                        setUploads([
                                            {
                                                file: f,
                                                progress: 0,
                                                status: 'pending',
                                                uploadId: uuidv4(),
                                                url: null
                                            }
                                        ])
                                    }}
                                    onCancel={() => { setCropMode(false) }}
                                    compression={{ quality: 1, maxWidth: 4000, maxHeight: 2000 }}
                                >
                                </ImageCropper>
                            </>
                        }
                        {
                            preview && Preview && !uploading && !multiple && <Preview preview={preview} />
                        }
                        {!cropMode &&
                            <>
                                <div className={styles.dropzoneLabel}>
                                    {msg}
                                </div>
                                <div className={styles.dropzoneActions}>
                                    <Button
                                        style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                        onClick={() => {
                                            if (ref && ref.current) {
                                                ref.current.open()
                                            }
                                        }}
                                    >
                                        {!iconButtons && lbl} <UploadIcon />
                                    </Button>
                                    {crop &&
                                        <Button
                                            style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                            onClick={() => {
                                                setCropMode(true)
                                            }}
                                        >
                                            {!iconButtons && 'RITAGLIA'} <CropIcon />
                                        </Button>
                                    }
                                </div>
                            </>
                        }
                    </div>
                </section>
            )}
        </Dropzone>
    )

}

export default Uploader